import { Component, OnInit, Input, Output, EventEmitter, Inject } from '@angular/core';
import { ElementOptionsAction } from './element-options-action.interface';
import { Observable } from 'rxjs';
import { ConstantsStatusType } from '../../../utilities/constants/constants.interface';
import { ProjectElement } from '../project-element.interface';
import { ElementService } from '../element.service';
import { AuthService } from '../../../auth/auth.service';
import { User } from '../../user/user.interface';
import { UserService } from '../../user/user.service';
import { ModalService } from '../../../services/modal/modal.service';
import { ElementSidebarService } from '../element-sidebar/element-sidebar.service';
import { RouterService } from '../../../utilities/router/router.service';

/**
 * Element options menu.
 *
 * @export
 */
@Component({
  selector: 'sg2-element-options',
  templateUrl: './element-options.component.html',
  styleUrls: ['./element-options.component.less']
})
export class ElementOptionsComponent implements OnInit {

  /** Default options. */
  private readonly OPTIONS_DEFAULT = ['assignTo', 'setStatus', 'delete', 'edit', 'showLogs'];

  /** Disabled state. */
  @Input() disabled = false;

  /** Element. */
  @Input() element: ProjectElement;

  /** Legacy support. */
  @Input() legacy: boolean;

  /** Set custom selection of options. */
  @Input() options: string[];

  /** Dispatch action. Kind of @depricated with new datastore. */
  @Output() action = new EventEmitter<ElementOptionsAction>();


  /** Configuration. */
  public config: {
    assignTo?: boolean;
    setStatus?: boolean;
    delete?: boolean;
    edit?: boolean;
    editLegacy?: boolean;
    restore?: boolean;
    restoreArea?: boolean;
    restoreAreaTemplate?: boolean;
    restoreParagraph?: boolean;
    showLogs?: boolean;
  } = {};

  /** Status types. */
  public statusTypes: ConstantsStatusType[];

  /** Project users. */
  public user$: Observable<User[]>;


  /** Constructor. */
  constructor(
    @Inject('sgConstants') sgConstantsService: any,
    public authService: AuthService,
    private elementService: ElementService,
    private elementSidebarService: ElementSidebarService,
    private modalService: ModalService,
    private routerService: RouterService,
    public userService: UserService
  ) {
    this.statusTypes = sgConstantsService.getList('STATUS_TYPES');
    this.user$ = this.userService.user$;
  }

  /** Set options on init. */
  ngOnInit(): void {
    this._setOptions();
    this._setState();
  }

  /**
   * Assign user to an element.
   *
   * @param element The element to assign the user to.
   * @param user The user (id) to assign to the element.
   */
  public assignToUser(element: ProjectElement, user: User['_id']): void {
    this.modalService.openAssignTo(element, { user: user }).afterClosed().subscribe(settings => {
      if (settings) {
        this.elementService.assignToWithOptions(element, settings);
      }
    });
  }

  /**
   * Delete element.
   *
   * @todo Bind to ElementService.delete(element);
   *
   * @param element The element to delete.
   */
  public delete(element: ProjectElement): void {
    this.elementService.delete(element, true);
  }

  /**
   * Edit element.
   *
   * @param element The element to edit.
   */
  public edit(element: ProjectElement): void {
    this.elementSidebarService.open(element);
  }

  /**
   * Edit element for legacy code.
   *
   * Triggers the legacy element-edit modal.
   *
   * @param element The element to edit.
   */
  public editLegacy(element: ProjectElement): void {
    this.action.emit({ action: 'edit', element: element });
  }

  /**
   * Restore element to template values.
   *
   * @todo bind to ElementService.delete(element);
   *
   * @param element The element to restore to default value.
   */
  public restoreArea(element: ProjectElement): void {
    this.elementService.delete(element, 'restore');
    this.action.emit({action: 'restoreArea', element: element});
  }

  /**
   * Restore element to template values.
   *
   * @todo bind to ElementService.delete(element);
   *
   * @param element The element to restore to default value.
   */
  public restoreAreaTemplate(element: ProjectElement): void {
    this.elementService.restore(element, true);
  }

  /**
   * Restore element to template values.
   *
   * @todo bind to ElementService.delete(element);
   *
   * @param element The element to restore to default value.
   */
  public restoreParagraph(element: ProjectElement): void {
    this.modalService.openRestoreParagraph(this.element).afterClosed().subscribe(restore => {
      if (restore) {
        const template = this.element.templateValues.find(tv => tv.quality === this.element.quality);
        this.elementService.setValue(element, template.value);
      }
    });
  }

  /**
   * Set element status.
   *
   * @param element The element to set status for.
   * @param status The status to set, as an id.
   */
  public setStatus(element: ProjectElement, status: ConstantsStatusType['_id']): void {
    !this.legacy
      ? this.elementService.setStatus(element, status)
      : this.action.emit({ action: 'setStatus', element: element, key: 'status', value:  status });
  }

  /**
   * Show logs and history.
   *
   * @param element The element to open the log for.
   */
  public showLogs(element: ProjectElement): void {
    this.action.emit({ action: 'showLogs', element: element });
    this.elementSidebarService.open(element, 'logs');
  }

  /**
   * Set menu options.
   *
   * A custom selection of options can be added by using the options binding.
   * When no options are given, the default selection is used.
   */
  private _setOptions(): void {
    this.options = this.options || this.OPTIONS_DEFAULT;
    this.options.forEach(o => {

      // Replace Delete with Restore for area requirements.
      if (o === 'delete') {
        if (this.elementService.isOfType(this.element, 'basicArea') && this.element.status !== 1) {
          o = 'restoreAreaTemplate';
        } else if (this.elementService.isOfType(this.element, 'basicAreaCopy')) {
          o = 'restoreArea';
        }
      }
      this.config[o] = true;

      // Hide Restore option when the paragraph is not edited.
      if (o === 'restoreParagraph') {
        this.config.restoreParagraph = this.elementService.isEdited(this.element);
      }
    });
  }

  /**
   * Ability to dynamically set states.
   *
   * Current checks:sg2ElementOptionsAction
   * - When viewing an area, any basic area elements should not have the options menu.
   */
  private _setState(): void {
    if (this.routerService.isActive('area') && this.elementService.isOfType(this.element, 'basicArea')) {
      this.disabled = true;
    }
  }
}
